import { useCallback, useEffect, useState } from 'react';
import FilterGroup, { FilterGroupState } from './filter-group/FilterGroup';
import FilterDialogHeader from './filter-dialog-header/FilterDialogHeader';
import FilterDialogActions from './filter-dialog-actions/FilterDialogActions';
import FilterDialogHiddenA11y from './filter-dialog-hidden-a11y/FilterDialogHiddenA11y';
import { FilterConfig, FilterDialogProps } from './model';
import { sendDataLinkEvent } from '../_helpers/dataLinkEventHelper.ts';
import { useComponentContext } from '../_helpers/componentContext.tsx';

import './FilterDialog.scss';

const FilterDialog: React.FC<FilterDialogProps> = ({
	filterConfigInput,
	filterStateInput,
	translationMap,
	filterChangeCallback,
	filterSubmitCallback,
	filterCancelCallback,
}) => {
	const componentName = useComponentContext().componentName;
	const [filterState, setFilterState] = useState<FilterGroupState[]>(
		initFilterState(filterConfigInput, filterStateInput)
	);
	const [numberOfResults, setNumberOfResults] = useState<number | undefined>(
		undefined
	);

	useEffect(() => {
		const result = filterChangeCallback(getFilterStateOutput(filterState));
		if (typeof result === 'object' && typeof result.then === 'function') {
			result.then((value: number) => setNumberOfResults(value));
		} else if (typeof result === 'number') {
			setNumberOfResults(result);
		}
	}, [filterChangeCallback, filterState]);

	const filterChangeHandler = useCallback((category: string, label: string) => {
		setFilterState((prevState) =>
			prevState.map((filterGroup) =>
				filterGroup.category === category
					? changeFilterGroupState(filterGroup, label)
					: filterGroup
			)
		);
	}, []);

	const resetFilter = useCallback(() => {
		setFilterState((prevState) =>
			prevState.map((filterGroup) => {
				return {
					category: filterGroup.category,
					filters: filterGroup.filters.map((filter) => ({
						...filter,
						checked: false,
					})),
				};
			})
		);
	}, []);

	const cancelAndClose = () => {
		filterCancelCallback();
	};

	const onSubmit = () => {
		filterSubmitCallback(getFilterStateOutput(filterState));
		sendDataLinkEvent(getSubmitDataLinkEvent(componentName, filterState));
	};

	return (
		<div className="blk-ac-filter-dialog blk-ac-modal-container">
			<FilterDialogHeader
				numberOfResults={numberOfResults}
				translationMap={translationMap}
			/>

			<div className="filter-dialog-content blk-ac-modal-content">
				{filterState.map((filterGroup, i) => {
					if (filterGroup.filters && filterGroup.filters.length) {
						return (
							<FilterGroup
								key={filterGroup.category}
								categoryIndex={i}
								category={filterGroup.category}
								categoryTitle={
									translationMap?.categoryTitles?.[filterGroup.category] ||
									filterGroup.category
								}
								ariaLabelForCategory={translationMap?.ariaLabelForCategory}
								filters={filterGroup.filters}
								onFilterGroupChange={filterChangeHandler}
							/>
						);
					} else {
						return '';
					}
				})}
			</div>

			<FilterDialogActions
				numberOfResults={numberOfResults}
				translationMap={translationMap}
				resetFilter={resetFilter}
				submitHandler={onSubmit}
				cancelHandler={cancelAndClose}
			/>

			<FilterDialogHiddenA11y
				numberOfResults={numberOfResults}
				translationMap={translationMap}
			/>
		</div>
	);
};

const initFilterState = (
	filterConfigInput: FilterConfig,
	filterStateInput: FilterConfig
): FilterGroupState[] => {
	const filterState: FilterGroupState[] = [];

	Object.entries(filterConfigInput).forEach(([category, labels]) => {
		const filterGroupState: FilterGroupState = {
			category,
			filters: [],
		};
		labels.forEach((label) => {
			filterGroupState.filters.push({
				label,
				checked: !!filterStateInput?.[category].includes(label),
			});
		});
		filterState.push(filterGroupState);
	});

	return filterState;
};

const getFilterStateOutput = (
	filterState: FilterGroupState[]
): FilterConfig => {
	const output: FilterConfig = {};

	filterState.forEach((filterGroupState) => {
		output[filterGroupState.category] = filterGroupState.filters
			.filter((filter) => filter.checked)
			.map((filter) => filter.label);
	});

	return output;
};

const getSubmitDataLinkEvent = (
	componentName: string,
	filterState: FilterGroupState[]
): string => {
	const filterList: string[] = [];

	filterState.forEach((filterGroupState) => {
		const checkedInFilterGroup: string[] = filterGroupState.filters
			.filter((filter) => filter.checked)
			.map((filter) => filter.label);
		filterList.push(...checkedInFilterGroup);
	});

	if (filterList.length) {
		return `${componentName}|filterBy|${filterList.join(', ')}`;
	} else {
		return `${componentName}|filterBy|no filter`;
	}
};

const changeFilterGroupState = (
	filterGroup: FilterGroupState,
	label: string
): FilterGroupState => {
	const filters = filterGroup.filters.map((filter) =>
		filter.label === label ? { label, checked: !filter.checked } : filter
	);
	return { category: filterGroup.category, filters };
};

export default FilterDialog;
